import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Api from '../../api';
import type {
  PartnerInviteValidation,
  PartnerRegistrationData
} from '../../api/partner';
import blueBerryLogoMini from '../../assets/images/bluberry_logo_mini.png';
import blueBerryLetters from '../../assets/images/bluberry.png';

const containerStyle = {
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  padding: '48px 16px'
};

const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  maxWidth: 520,
  padding: '40px',
  width: '100%'
};

function PartnerRegistration() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<PartnerInviteValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<PartnerRegistrationData>({
    addressLine1: '',
    city: '',
    contactFirstName: '',
    contactLastName: '',
    contactPhone: '',
    country: '',
    description: '',
    state: '',
    zipCode: ''
  });

  useEffect(() => {
    if (!token) return;
    Api.PartnerApi.validateInviteToken(token)
      .then((data) => {
        if (!data.valid) {
          setError('This invitation has expired or has already been used.');
        }
        setInvite(data);
      })
      .catch(() => {
        setError(
          'Invalid invitation link. Please contact the company that invited you.'
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  const onChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.contactFirstName.trim() || !form.contactLastName.trim()) {
      setError('First name and last name are required.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const result = await Api.PartnerApi.completeRegistration(token!, form);
      navigate(`/partner-portal/${result.accessToken}`);
    } catch (e: any) {
      setError(
        e?.response?.data?.message || 'Registration failed. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container sx={containerStyle}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={containerStyle}>
      <Box sx={cardStyle}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            justifyContent: 'center',
            mb: 3
          }}
        >
          <img alt="Bluberry" src={blueBerryLogoMini} style={{ height: 32 }} />
          <img alt="Bluberry" src={blueBerryLetters} style={{ height: 20 }} />
        </Box>

        <Typography
          sx={{ fontWeight: 500, mb: 1, textAlign: 'center' }}
          variant="h5"
        >
          Partner registration
        </Typography>

        {invite && !error && (
          <Typography
            color="text.secondary"
            sx={{ mb: 3, textAlign: 'center' }}
            variant="body2"
          >
            <strong>{invite.tenantCompanyName}</strong> has invited{' '}
            <strong>{invite.companyName}</strong> to join their partner network.
            Complete your profile below.
          </Typography>
        )}

        {error && (
          <Typography
            color="error"
            sx={{ mb: 2, textAlign: 'center' }}
            variant="body2"
          >
            {error}
          </Typography>
        )}

        {invite?.valid && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                required
                label="First name"
                onChange={(e) => onChange('contactFirstName', e.target.value)}
                size="small"
                value={form.contactFirstName}
              />
              <TextField
                fullWidth
                required
                label="Last name"
                onChange={(e) => onChange('contactLastName', e.target.value)}
                size="small"
                value={form.contactLastName}
              />
            </Box>
            <TextField
              fullWidth
              label="Phone"
              onChange={(e) => onChange('contactPhone', e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              value={form.contactPhone}
            />
            <TextField
              fullWidth
              label="Address"
              onChange={(e) => onChange('addressLine1', e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              value={form.addressLine1}
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="City"
                onChange={(e) => onChange('city', e.target.value)}
                size="small"
                value={form.city}
              />
              <TextField
                fullWidth
                label="State"
                onChange={(e) => onChange('state', e.target.value)}
                size="small"
                value={form.state}
              />
              <TextField
                label="Zip"
                onChange={(e) => onChange('zipCode', e.target.value)}
                size="small"
                sx={{ width: 140 }}
                value={form.zipCode}
              />
            </Box>
            <TextField
              fullWidth
              multiline
              label="Company description"
              onChange={(e) => onChange('description', e.target.value)}
              rows={3}
              size="small"
              sx={{ mb: 3 }}
              value={form.description}
            />
            <Button
              fullWidth
              disabled={submitting}
              onClick={handleSubmit}
              sx={{ textTransform: 'none' }}
              variant="contained"
            >
              {submitting ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                'Complete registration'
              )}
            </Button>
          </>
        )}
      </Box>
    </Container>
  );
}

export default PartnerRegistration;
